// BUG-21 — Per-page "?" help button.
//
// Drop <T2THelpButton routeKey="admin.users" /> next to a page title and a
// circular ? icon appears. Click → popover with title, what-it-does paragraph,
// 2-4 action bullets, optional "Watch a short walkthrough" link.
//
// Hides itself silently if helpContent.js has no entry for the routeKey, so
// it's safe to drop in even when content is still being authored.

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { getHelpContent } from "./helpContent";

const T2THelpButton = ({ routeKey, label = "Page help" }) => {
  const content = getHelpContent(routeKey);
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef  = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target))  return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!content) return null;

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: "1px solid var(--t2t-border)",
          background: open ? "var(--t2t-primary)" : "var(--t2t-bg, #fff)",
          color: open ? "#fff" : "var(--t2t-text-muted)",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.15s ease, color 0.15s ease",
          flexShrink: 0,
        }}
      >
        ?
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`${content.title} help`}
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            // BUG-21 fix: anchor to LEFT edge of the button so the popover extends
            // rightward into the page. (Was right: 0, which made it extend
            // leftward off-screen when the button sits in the upper-left.)
            left: 0,
            width: 360,
            maxWidth: "min(360px, 90vw)",
            background: "#fff",
            border: "1px solid var(--t2t-border)",
            borderRadius: 8,
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12)",
            padding: 16,
            zIndex: 1000,
            fontSize: 12,
            lineHeight: 1.55,
            color: "var(--t2t-text)",
            textAlign: "left",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--t2t-text)" }}>{content.title}</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close help"
              style={{
                width: 20, height: 20, borderRadius: 4,
                border: "none", background: "transparent", cursor: "pointer",
                color: "var(--t2t-text-muted)", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0,
              }}
            >×</button>
          </div>

          <p style={{ margin: "0 0 10px", color: "var(--t2t-text-secondary)" }}>
            {content.body}
          </p>

          {Array.isArray(content.actions) && content.actions.length > 0 && (
            <ul style={{ margin: "0 0 10px", paddingLeft: 18, color: "var(--t2t-text-secondary)" }}>
              {content.actions.map((a, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{a}</li>
              ))}
            </ul>
          )}

          {content.videoUrl && (
            <a
              href={content.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 11, fontWeight: 600,
                color: "var(--t2t-primary)", textDecoration: "none",
                padding: "4px 0",
              }}
            >
              ▶ Watch a short walkthrough
            </a>
          )}
        </div>
      )}
    </span>
  );
};

T2THelpButton.propTypes = {
  routeKey: PropTypes.string.isRequired,
  label:    PropTypes.string,
};

export default T2THelpButton;
